import React, { useState, useEffect } from 'react';
import { getRoundVerdicts, getMyVerdicts, submitVerdict, updateVerdict, deleteVerdict } from '../../api';
import HrnbCard from '../controls/HrnbCard';
import HrnbButton from '../controls/HrnbButton';
import HrnbInput from '../controls/HrnbInput';
import HrnbTextArea from '../controls/HrnbTextArea';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbModal from '../controls/HrnbModal';
import HrnbFormControl from '../controls/HrnbFormControl';
import HrnbLabel from '../controls/HrnbLabel';
import '../../css/duels/DuelJudgeVerdicts.css';

/**
 * Компонент вердиктов судей для раунда дуэли
 */
function DuelJudgeVerdicts({ duelId, roundId, isJudge = false }) {
    const [verdicts, setVerdicts] = useState(null);
    const [myVerdicts, setMyVerdicts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [editingVerdict, setEditingVerdict] = useState(null);
    const [formData, setFormData] = useState({
        trackId: '',
        score: 5,
        reasoning: ''
    });
    const [submitting, setSubmitting] = useState(false);

    // Загрузка вердиктов
    useEffect(() => {
        loadVerdicts();
    }, [duelId, roundId]);

    const loadVerdicts = async () => {
        setLoading(true);
        setError(null);

        try {
            const [verdictsData, myVerdictsData] = await Promise.all([
                getRoundVerdicts(roundId),
                isJudge ? getMyVerdicts(roundId) : Promise.resolve([])
            ]);

            setVerdicts(verdictsData);
            setMyVerdicts(myVerdictsData || []);
        } catch (err) {
            console.error('[DuelJudgeVerdicts] Failed to load verdicts:', err);
            setError('Не удалось загрузить вердикты');
        } finally {
            setLoading(false);
        }
    };

    // Открытие модального окна для нового вердикта
    const handleOpenNewVerdict = (trackId) => {
        setEditingVerdict(null);
        setFormData({
            trackId,
            score: 5,
            reasoning: ''
        });
        setShowModal(true);
    };

    // Открытие модального окна для редактирования
    const handleEditVerdict = (verdict) => {
        setEditingVerdict(verdict);
        setFormData({
            trackId: verdict.trackId,
            score: verdict.score,
            reasoning: verdict.reasoning
        });
        setShowModal(true);
    };

    // Закрытие модального окна
    const handleCloseModal = () => {
        setShowModal(false);
        setEditingVerdict(null);
        setFormData({
            trackId: '',
            score: 5,
            reasoning: ''
        });
        setError(null);
        setSuccess(null);
    };

    // Отправка вердикта
    const handleSubmit = async () => {
        if (!formData.reasoning.trim()) {
            setError('Обоснование обязательно');
            return;
        }

        if (formData.score < 1 || formData.score > 10) {
            setError('Оценка должна быть от 1 до 10');
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            if (editingVerdict) {
                await updateVerdict(editingVerdict.id, formData);
                setSuccess('Вердикт обновлён');
            } else {
                await submitVerdict(roundId, formData);
                setSuccess('Вердикт выставлен');
            }

            handleCloseModal();
            loadVerdicts();

            // Очистка успеха через 3 секунды
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            console.error('[DuelJudgeVerdicts] Failed to submit verdict:', err);
            setError(err.response?.data?.message || 'Не удалось сохранить вердикт');
        } finally {
            setSubmitting(false);
        }
    };

    // Удаление вердикта
    const handleDelete = async (verdictId) => {
        if (!window.confirm('Вы уверены, что хотите удалить вердикт?')) return;

        try {
            await deleteVerdict(verdictId);
            setSuccess('Вердикт удалён');
            loadVerdicts();
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            console.error('[DuelJudgeVerdicts] Failed to delete verdict:', err);
            setError(err.response?.data?.message || 'Не удалось удалить вердикт');
        }
    };

    if (loading) {
        return <HrnbLoader />;
    }

    if (!verdicts) {
        return <HrnbAlert variant="info">Вердикты ещё не выставлены</HrnbAlert>;
    }

    const { trackScores = [], totalVerdicts = 0 } = verdicts;

    return (
        <div className="duel-judge-verdicts">
            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            {success && (
                <HrnbAlert variant="success" onClose={() => setSuccess(null)}>
                    {success}
                </HrnbAlert>
            )}

            {/* Сводка */}
            <div className="duel-judge-verdicts__summary">
                <div className="duel-judge-verdicts__total">
                    Всего вердиктов: <strong>{totalVerdicts}</strong>
                </div>
                <div className="duel-judge-verdicts__tracks">
                    Оценено треков: <strong>{trackScores.length}</strong>
                </div>
            </div>

            {/* Оценки по трекам */}
            {trackScores.length > 0 && (
                <div className="duel-judge-verdicts__track-scores">
                    <h3 className="duel-judge-verdicts__subtitle">Оценки по трекам</h3>
                    <div className="duel-judge-verdicts__track-scores-list">
                        {trackScores.map((trackScore, index) => {
                            const hasMyVerdict = myVerdicts.some(v => v.trackId === trackScore.trackId);
                            
                            return (
                                <div key={trackScore.trackId} className="duel-judge-verdicts__track-score">
                                    <div className="duel-judge-verdicts__track-info">
                                        <span className="duel-judge-verdicts__track-order">
                                            #{trackScore.playOrder || index + 1}
                                        </span>
                                        <span className="duel-judge-verdicts__track-name">
                                            {trackScore.participantName || `Участник ${index + 1}`}
                                        </span>
                                    </div>

                                    <div className="duel-judge-verdicts__track-stats">
                                        <div className="duel-judge-verdicts__score">
                                            <span className="duel-judge-verdicts__score-value">
                                                {trackScore.averageScore?.toFixed(1) || '—'}
                                            </span>
                                            <span className="duel-judge-verdicts__score-label">
                                                Средний балл
                                            </span>
                                        </div>
                                        <div className="duel-judge-verdicts__total-score">
                                            <span className="duel-judge-verdicts__total-score-value">
                                                {trackScore.totalScore || 0}
                                            </span>
                                            <span className="duel-judge-verdicts__total-score-label">
                                                Сумма
                                            </span>
                                        </div>
                                        <div className="duel-judge-verdicts__votes">
                                            <span className="duel-judge-verdicts__votes-value">
                                                {trackScore.verdictsCount || 0}
                                            </span>
                                            <span className="duel-judge-verdicts__votes-label">
                                                Оценок
                                            </span>
                                        </div>
                                    </div>

                                    {isJudge && !hasMyVerdict && (
                                        <HrnbButton
                                            variant="primary"
                                            size="sm"
                                            onClick={() => handleOpenNewVerdict(trackScore.trackId)}
                                        >
                                            Оценить
                                        </HrnbButton>
                                    )}

                                    {isJudge && hasMyVerdict && (
                                        <div className="duel-judge-verdicts__track-actions">
                                            <HrnbButton
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => handleEditVerdict(
                                                    myVerdicts.find(v => v.trackId === trackScore.trackId)
                                                )}
                                            >
                                                Изменить
                                            </HrnbButton>
                                            <HrnbButton
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleDelete(
                                                    myVerdicts.find(v => v.trackId === trackScore.trackId).id
                                                )}
                                            >
                                                Удалить
                                            </HrnbButton>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Список всех вердиктов */}
            {verdicts.verdicts && verdicts.verdicts.length > 0 && (
                <div className="duel-judge-verdicts__list">
                    <h3 className="duel-judge-verdicts__subtitle">Все вердикты</h3>
                    <div className="duel-judge-verdicts__verdicts">
                        {verdicts.verdicts.map((verdict) => (
                            <div key={verdict.id} className="duel-judge-verdicts__verdict">
                                <div className="duel-judge-verdicts__verdict-header">
                                    <span className="duel-judge-verdicts__judge">
                                        👤 {verdict.judgeName}
                                    </span>
                                    <span className="duel-judge-verdicts__score-badge">
                                        {verdict.score}/10
                                    </span>
                                </div>
                                <div className="duel-judge-verdicts__track">
                                    Трек: {verdict.participantName}
                                </div>
                                <div className="duel-judge-verdicts__reasoning">
                                    {verdict.reasoning}
                                </div>
                                <div className="duel-judge-verdicts__date">
                                    {new Date(verdict.createdAt).toLocaleString('ru-RU')}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Модальное окно вердикта */}
            <HrnbModal
                show={showModal}
                onHide={handleCloseModal}
                title={editingVerdict ? 'Редактировать вердикт' : 'Выставить вердикт'}
                footer={
                    <>
                        <HrnbButton variant="secondary" onClick={handleCloseModal} disabled={submitting}>
                            Отмена
                        </HrnbButton>
                        <HrnbButton variant="primary" onClick={handleSubmit} disabled={submitting}>
                            {submitting ? 'Сохранение...' : 'Сохранить'}
                        </HrnbButton>
                    </>
                }
            >
                <HrnbFormControl>
                    <HrnbLabel>Оценка (1-10) *</HrnbLabel>
                    <HrnbInput
                        type="number"
                        min="1"
                        max="10"
                        value={formData.score}
                        onChange={(e) => setFormData(prev => ({ ...prev, score: parseInt(e.target.value) || 0 }))}
                        required
                    />
                </HrnbFormControl>

                <HrnbFormControl>
                    <HrnbLabel>Обоснование *</HrnbLabel>
                    <HrnbTextArea
                        value={formData.reasoning}
                        onChange={(e) => setFormData(prev => ({ ...prev, reasoning: e.target.value }))}
                        placeholder="Опишите, почему вы поставили такую оценку..."
                        rows={5}
                        required
                    />
                    <small className="duel-judge-verdicts__hint">
                        Минимум 10 символов. Будьте объективны и тактичны.
                    </small>
                </HrnbFormControl>
            </HrnbModal>
        </div>
    );
}

export default DuelJudgeVerdicts;
